import { omitUndefinedProperties } from './objects';
import { isObject } from './types';

export function joinStr(clauses: string[], finalConj: string = 'and'): string {
    if (!clauses.length) return '';
    if (clauses.length === 1) return clauses[0];
    if (clauses.length === 2) return clauses.join(` ${finalConj} `);

    const startWords = clauses.slice(0, clauses.length - 2);
    const finalWords = clauses.slice(clauses.length - 2);

    return startWords.join(', ') + ', ' + finalWords.join(` ${finalConj} `);
}

export function toCamelCase(val: string, delimeter = '_') {
    const [firstWord, ...otherWords] = val.split(delimeter);
    return [
        firstWord.toLowerCase(),
        otherWords
            .map(
                (word) =>
                    word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(''),
    ].join('');
}

export function toPascalCase(val: string, delimeter = '_') {
    const camelVal = toCamelCase(val, delimeter);
    return camelVal.slice(0, 1).toUpperCase() + camelVal.slice(1);
}

export function toTitleCase(val: string, delimeter = '_') {
    const words = val.split(delimeter);
    return words.map(capitalize).join(' ');
}

export function toSentenceCase(val: string, delimeter = '_') {
    const words = val.split(delimeter);
    return capitalize(words.join(' '));
}

export const camelToSnakeCase = (str: string) =>
    str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const capitalize = (val: string): string => {
    return val.slice(0, 1).toUpperCase() + val.slice(1).toLowerCase();
};

// 'FooBar' => 'Foo bar'
export const pascaleToSentenceCase = (str: string): string => {
    return str
        .split(/(?=[A-Z])/)
        .map((word, i) =>
            i === 0
                ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                : word.toLowerCase()
        )
        .join(' ');
};

export const toUpperFirst = (val: string): string => {
    return val.slice(0, 1).toUpperCase() + val.slice(1);
};

export const toLowerFirst = (val: string): string => {
    return val.slice(0, 1).toLowerCase() + val.slice(1);
};

export const toLowerCasePreservingAcronyms = (val: string) => {
    return val
        .split(' ') // split into words
        .map(
            (s) =>
                // Is every character upper case?
                Array.from(s).every((c) => c === c.toUpperCase())
                    ? s // Yes: return as-is (upper case)
                    : s.toLowerCase() // No: return lower case
        )
        .join(' '); // join words;
};

export const stringLiteralToTitleCase = (str?: string) =>
    toTitleCase((str || '').replace('_', ' '));

export const digitsFromStr = (input: string): string =>
    input.replace(/[^0-9.]/g, '');

export const escapeRegex = (input: string) =>
    input.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&');

// Given an object, return a query string, omitting undefined properties
// Example: { a: 1, b: undefined, c: 3 } => '?a=1&c=3'
export const buildQueryString = (queryParams?: unknown): string => {
    if (!isObject(queryParams)) return '';

    const filteredQueryParams = omitUndefinedProperties(queryParams);

    return Object.keys(filteredQueryParams).length === 0
        ? ''
        : `?${new URLSearchParams(
              filteredQueryParams as Record<string, string>
          )}`;
};

export const getIsValidZipCode = (zipCode: string): boolean => {
    return /^\d{5}$/.test(zipCode);
};

export const getIsValidUuid = (uuid: string): boolean => {
    return /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(
        uuid
    );
};

// Ref: https://stackoverflow.com/questions/18862256/how-to-detect-emoji-using-javascript
export const hasEmoji = (str?: string) =>
    str &&
    (/\p{Extended_Pictographic}/u.test(str) ||
        /\p{Regional_Indicator}/u.test(str));
